import React, { useContext, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import moment from 'moment';
import StatusChartWidget from '../../../components/Widgets/StatusChartWidget';
import { LarianTechAssetWidget } from '../../../components/Widgets/LarianTechWidget';
import TimelineWidget from '../../../components/Widgets/TimelineWidget';
import OverviewWidget from '../../../components/Widgets/OverviewWidget';
import ContractWidget from '../../../components/Widgets/ContractWidget';
import WidgetDashboard from '../../../components/WidgetDashboard';
import SettingsContext from '../../../context/SettingsContext';
import useAsync from '../../../hooks/useAsync';
import { getWidgetsByType } from '../../../api/company';
import {
	getAvailableWidgets,
	TAG_COLOR_OPTIONS,
} from '../../../components/helpers/constants';
import { isEqual } from 'lodash';
import { getModuleCategories, getModuleFieldsBeta } from '../../../api/modules';
import {
	getLocations,
	updateAsset,
	getEventsByAsset,
	getTickets,
	addAssetDocument,
	addAssetContacts,
	removeAssetDocument,
} from '../../../api';
import {
	classNames,
	createDataTree,
	getUserFullName,
	objectAccess,
} from '../../../components/helpers/functions';
import ReactSelect, { components } from 'react-select';
import {
	ChevronDownIcon,
	ChevronLeftIcon,
	ChevronRightIcon,
	PlusCircleIcon,
	PlusIcon,
	XMarkIcon,
	DocumentIcon,
	CloudArrowUpIcon,
	UsersIcon,
	ClockIcon,
	PaperClipIcon,
	CheckCircleIcon,
	TicketIcon,
	DocumentTextIcon,
	WrenchScrewdriverIcon,
	ClipboardDocumentListIcon,
	EllipsisVerticalIcon,
} from '@heroicons/react/24/outline';
import { CheckCircleIcon as CheckCircleSolidIcon } from '@heroicons/react/20/solid';
import { AvatarGenerated } from '../../../components/Avatar';
import usePrevious from '../../../hooks/usePrevious';
import Button from '../../../components/Button';
import useModal from '../../../hooks/useModal';
import {
	TemplateField,
	TemplateReactSelect,
} from '../../../components/Attributes/Attributes_old';
import { getContacts } from '../../../api/contacts';
import { getDocument, getDocuments } from '../../../api/documents';
import {
	getContracts,
	addAssetToContract,
	removeAssetFromContract,
} from '../../../api/contracts';
import { getWorkOrders } from '../../../api/workOrders';
import { getTasks, updateTask } from '../../../api/tasks';
import { getPMSchedules, removePMScheduleAsset } from '../../../api/pmSchedules';
import useSettings from '../../../hooks/useSettings';
import { getTags } from '../../../api/tags';
import Badge from '../../../components/Badge';
import { StatusBadge } from '../../../components/nextgen/Badge';
import { FileUpload } from '../../../components/nextgen';
import { Menu } from '@headlessui/react';
import { useApp } from '../../../context/AppContextBeta';

const ASSETS_MODULE_ID = process.env.REACT_APP_MODULE_ID_ASSETS;
const DOCUMENTS_MODULE_ID = process.env.REACT_APP_MODULE_ID_DOCUMENTS;

const DropdownIndicator = (props) => {
	return (
		<components.DropdownIndicator {...props}>
			<ChevronDownIcon className="w-4 h-4 text-gray-500" />
		</components.DropdownIndicator>
	);
};

const ClearIndicator = (props) => {
	return (
		<components.ClearIndicator {...props}>
			<XMarkIcon className="w-8 h-8 text-gray-500 px-2 py-1 cursor-pointer" />
		</components.ClearIndicator>
	);
};

const CustomMenuList = ({ customAction, ...props }) => {
	return (
		<components.MenuList {...props}>
			{props.children}
			{customAction ? (
				<>
					<div className="border-t border-gray-200">
						<Button
							type="ghost"
							Icon={PlusIcon}
							onClick={() => customAction?.onClick()}
						>
							{customAction?.label}{' '}
						</Button>
					</div>
				</>
			) : null}
		</components.MenuList>
	);
};

// This is used to find a nested object by ID and return the parent objects
function findPathById(items, targetId, path = []) {
	for (const item of items) {
		const newPath = [...path, item];
		if (item.id === targetId) {
			return newPath;
		}
		if (item.children) {
			const result = findPathById(item.children, targetId, newPath);
			if (result) return result;
		}
	}
	return null;
}

export default ({ item, refreshAllAssets, refreshItem }) => {
	const {
		settings,
		loading: isLoadingSettings,
		update: updateSettings,
	} = useContext(SettingsContext);

	console.log('settings: ', settings);

	const [extended, setExtended] = useState(
		settings?.user?.extendedAssetAttributes,
	);

	const { role } = useApp();

	const isSuperAdmin = role?.name === 'default:superadmin';

	const [form, setForm] = useState({});

	const [editing, setEditing] = useState(null);

	const [fileList, setFileList] = useState([]);
	const [documentsPage, setDocumentsPage] = useState(1);

	const { setOpen: openNewCategory } = useModal('addCategoryModal');
	const { open, setOpen: openLocationModal } = useModal('addLocation');
	const { setOpen: openNewVendor } = useModal('addVendorModal');
	const { setOpen: openVendorPicker } = useModal('vendorPicker');
	const { open: selectLocationOptions, setOpen: openSelectLocation } =
		useModal('selectLocation');
	const { setOpen: openManageAttributes } = useModal('manageAttributes');

	const { value: widgets, status: isLoadingWidgets } = useAsync(
		getWidgetsByType,
		{
			defaultPayload: { type: 'assetDashboard' },
		},
	);

	const { value: tags, status: isLoadingTags } = useAsync(getTags);

	const {
		value: moduleFields,
		execute: onRefreshModuleFields,
		status: isLoadingModuleFields,
	} = useAsync(getModuleFieldsBeta, {
		defaultPayload: { id: ASSETS_MODULE_ID, active: true },
	});

	const {
		execute: onUpdateAsset,
		status: isUpdatingAsset,
		error: hasUpdatingError,
	} = useAsync(updateAsset, {
		forceCallback: true,
		immediate: false,
		showNotification: { success: 'Asset has been updated', error: 'raw' },
	});

	const { value: locations, status: isLoadingLocations } =
		useAsync(getLocations);

	const { value: categories, status: isLoadingCategories } = useAsync(
		getModuleCategories,
		{ defaultPayload: { id: ASSETS_MODULE_ID } },
	);

	const { value: vendors, status: isLoadingVendors } = useAsync(getContacts);

	const navigate = useNavigate();
	const { id } = useParams();

	const { setOpen: openUploadDocumentsModal } = useModal('uploadDocuments');
	const { setOpen: openDocumentPicker } = useModal('documentPicker');
	const { setOpen: openDocumentPreview, setClose: closeDocumentPreview } =
		useModal('documentPreview');
	const { setOpen: onViewContact } = useModal('viewContact');
	const { setOpen: onOpenTicket } = useModal('ticketOverview');
	const { setOpen: onOpenContract } = useModal('contractOverview');
	const { setOpen: onOpenEvent } = useModal('eventDetails');
	const { setOpen: onOpenWorkOrder } = useModal('workOrderOverview');
	const { setOpen: onOpenTask } = useModal('taskDetails');
	const { setOpen: openCreateTicket } = useModal('createTicket');
	const { setOpen: openCreateWorkOrder } = useModal('createWorkOrder');
	const { setOpen: openContractPicker } = useModal('contractPicker');
	const { setOpen: openAddContract } = useModal('contractAdd');
	const { setOpen: onOpenPMSchedule } = useModal('pmScheduleOverview');
	const { setOpen: openEditPMSchedule } = useModal('editPMSchedule');

	// Fetch documents for this asset
	const { value: documents, execute: refreshDocuments } = useAsync(
		getDocuments,
		{
			...(item?.id
				? { defaultPayload: { assetId: item?.id, limit: 5 } }
				: { immediate: false }),
			silent: true,
		},
	);

	// Add documents to asset
	const { execute: onAddDocuments } = useAsync(addAssetDocument, {
		immediate: false,
	});

	// Remove documents from asset
	const { execute: onRemoveDocument } = useAsync(removeAssetDocument, {
		immediate: false,
	});

	// Add contacts/vendors to asset
	const { execute: onAddContacts } = useAsync(addAssetContacts, {
		immediate: false,
	});

	// Link contract to asset
	const { execute: onLinkContract } = useAsync(addAssetToContract, {
		immediate: false,
	});

	// Remove contract from asset
	const { execute: onRemoveContract } = useAsync(removeAssetFromContract, {
		immediate: false,
	});

	// Fetch timeline data
	const { value: events } = useAsync(getEventsByAsset, {
		defaultPayload: { assetId: item?.id },
		silent: true,
	});

	const { value: tickets, execute: refreshTickets } = useAsync(getTickets, {
		defaultPayload: { assetId: item?.id },
		silent: true,
	});

	const { value: contracts, execute: refreshContracts } = useAsync(
		getContracts,
		{
			defaultPayload: { assetId: item?.id },
			silent: true,
		},
	);

	// Fetch work orders for this asset
	const { value: workOrders, execute: refreshWorkOrders } = useAsync(
		getWorkOrders,
		{
			defaultPayload: { assetId: item?.id },
			silent: true,
		},
	);

	// Fetch PM schedules for this asset
	const { value: pmSchedules, execute: refreshPMSchedules } = useAsync(
		getPMSchedules,
		{
			defaultPayload: { assetId: item?.id },
			silent: true,
		},
	);

	// Remove PM schedule from asset
	const { execute: onRemovePMSchedule } = useAsync(removePMScheduleAsset, {
		immediate: false,
	});

	// Fetch open tasks for this asset
	const { value: tasks, execute: refreshTasks } = useAsync(getTasks, {
		defaultPayload: { status: ['pending', 'in_progress'], assetId: item?.id },
		silent: true,
	});

	// Handler for toggling task completion
	const handleToggleTask = async (taskId, currentCompleted) => {
		try {
			await updateTask(taskId, {
				status: currentCompleted ? 'pending' : 'completed',
			});
			// Refresh tasks list
			refreshTasks({ status: ['pending', 'in_progress'] });
		} catch (error) {
			console.error('Failed to update task:', error);
		}
	};

	// Helper function to open document preview with edit capabilities
	const openDocumentPreviewWithEdit = async (documentId) => {
		const doc = await getDocument({ id: documentId });
		openDocumentPreview({
			document: doc,
			enableEdit: true,
			enableRemove: true,
			onEdit: (document) => {
				openUploadDocumentsModal({
					id: document.id,
					moduleId: DOCUMENTS_MODULE_ID,
					onSubmit: async () => {
						await refreshDocuments({ assetId: item?.id, limit: 5 });
						// Close the preview modal
						closeDocumentPreview();
						// Wait for modal to close and data to update, then reopen with fresh data
						setTimeout(async () => {
							await openDocumentPreviewWithEdit(document.id);
						}, 300);
					},
				});
			},
			onRemove: (document) => {
				onRemoveDocument(
					{
						id: item?.id,
						documentId: document.id,
					},
					() => {
						refreshDocuments({ assetId: item?.id, limit: 5 });
					},
				);
			},
		});
	};

	// Build timeline items from all sources
	const buildTimelineItems = () => {
		const items = [];

		// Add asset creation
		if (item?.createdAt) {
			items.push({
				id: 'created',
				type: 'created',
				title: 'Asset created',
				subtitle: `${item?.manufacturerName || item?.manufacturer?.name} ${item?.modelName || item?.model?.name}`,
				date: item.createdAt,
				sortDate: new Date(item.createdAt),
			});
		}

		// Add tickets
		tickets?.rows?.forEach((ticket) => {
			items.push({
				id: ticket.id,
				type: 'ticket',
				title: ticket.title || 'Ticket',
				description: ticket.description,
				status: ticket.status,
				date: ticket.createdAt,
				sortDate: new Date(ticket.createdAt),
			});
		});

		// Add contracts
		contracts?.rows?.forEach((contract) => {
			const isExpired = moment().isAfter(contract?.endDate);
			items.push({
				id: contract.id,
				type: 'contract',
				title: contract.name || 'Contract',
				subtitle: contract.vendor?.companyName,
				description: `${moment(contract.startDate).format('MMM D, YYYY')} - ${moment(contract.endDate).format('MMM D, YYYY')}`,
				active: contract.active,
				isExpired,
				date: contract.startDate,
				sortDate: new Date(contract.startDate),
			});
		});

		// Add services/events
		events?.forEach((event) => {
			const eventDate =
				event.completedAt ||
				event.scheduledDate ||
				event.estimatedDate ||
				event.createdAt;
			items.push({
				id: event.id,
				type: 'service',
				title: event.title || event.service?.name || 'Service',
				description: event.category?.name,
				status: event.status,
				date: eventDate,
				sortDate: new Date(eventDate),
			});
		});

		// Sort by date descending (most recent first)
		return items.sort((a, b) => b.sortDate - a.sortDate);
	};

	const timelineItems = buildTimelineItems();

	useEffect(() => {
		if (hasUpdatingError) {
			setForm(convertRawAssetToModulizizedForm(item));
		}
	}, [hasUpdatingError]);

	useEffect(() => {
		if (
			settings &&
			typeof settings?.user?.extendedAssetAttributes !== 'undefined'
		) {
			setExtended(settings?.user?.extendedAssetAttributes);
		}
	}, [settings]);

	const convertRawAssetToModulizizedForm = (asset) => {
		let itemForm = {};
		moduleFields?.combined?.map((field) => {
			const isDefault = field?.isDefault;

			if (isDefault) {
				if (asset[field?.key] || asset[`${field?.key}Id`]) {
					itemForm[field?.key] = {
						fieldId: field?.id,
						value:
							asset[`${field?.key}_id`] || asset[`${field?.key}Id`]
								? asset[field?.key]?.name
								: asset[field?.key],
					};
				}
			} else {
				const val = asset?.custom_attributes?.find(
					(f) => f.attribute_field_id === field.id,
				);
				itemForm[field?.key] = {
					value: val?.backupValue || val?.value,
					fieldId: field.id,
					id: val?.id,
				};
			}
		});
		return itemForm;
	};

	const reducedAttributes = moduleFields?.combined
		?.filter((field) => field?.label !== 'Tags')
		?.reduce(
			(prev, cur) => {
				//if (form[cur?.key]?.value) {
				prev[cur?.isDefault ? 'default' : 'custom'].push(cur);
				//}
				return prev;
			},
			{ default: [], custom: [] },
		);

	const getPath = {
		manufacturer: {
			path: 'manufacturer.name',
		},
		model: {
			path: 'model.name',
		},
		location: {
			path: 'location.id',
		},
		category: {
			path: 'category.id',
		},
	};

	const getValue = (field, obj) => {
		if (field?.isDefault) {
			return objectAccess(getPath[field?.key]?.path || field?.key, obj || item);
		} else {
			return (obj || item)?.custom_attributes?.find(
				(o) => o.attribute_field_id === field?.id,
			)?.originalValue;
		}
	};

	const vendorOptions = vendors?.rows?.map((vendor) => ({
		label: vendor?.companyName || getUserFullName(vendor),
		value: vendor?.id,
	}));

	const categoryOptions = categories?.map((category) => ({
		label: category?.name,
		value: category?.id,
	}));

	const booleanOptions = [
		{ label: 'True', value: 'true' },
		{ label: 'False', value: 'false' },
	];

	const locationOptionsTree = createDataTree(locations?.rows, 'children');

	console.log('widgetSettings: ', settings?.user);
	console.log(
		'widgets: ',
		widgets,
		getAvailableWidgets(
			'assetDashboard',
			widgets?.map((w) => w.key),
		),
	);

	return (
		<div className="sm:mt-4 mb-10 pb-20">
			<div className="grid grid-cols-5 gap-x-6">
				<div className="col-span-2">
					<ul role="list" className="divide-y divide-gray-100 text-xs">
						{reducedAttributes?.default?.map((field) => {
							const value = getValue(field) || '-';
							const isEditable = field?.isDefault
								? !['manufacturer', 'model'].includes(field?.key)
								: true;

							return (
								<li className="flex flex-wrap items-center justify-between gap-x-6 gap-y-4  sm:flex-nowrap text-xs">
									<div className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 flex-1">
										<div className="text-gray-500 pt-2">{field?.label}</div>
										<div className="">
											{isEditable ? (
												field?.type === 'text' ? (
													<input
														value={
															editing?.key === field?.key
																? editing?.newValue
																: value
														}
														className={classNames(
															'outline-1 focus:outline-none focus:-outline-offset-2 hover:-outline-offset-1 outline-none -outline-offset-2 hover:outline-gray-400 focus:outline-indigo-500 py-2.5 px-0 sm:px-3 h-full w-full',
														)}
														onChange={(e) => {
															setEditing({
																key: field?.key,
																newValue: e?.target?.value,
															});
														}}
														onFocus={() => {
															setEditing({ key: field?.key });
														}}
														onBlur={() => {
															if (
																editing.key === field?.key &&
																typeof editing?.newValue !== 'undefined' &&
																editing?.newValue !== getValue(field, item)
															) {
																onUpdateAsset(
																	{
																		id: item?.id,
																		[editing?.key]: editing?.newValue,
																	},
																	(payload, err) => {
																		if (err) {
																			setEditing({
																				...editing,
																				newValue: getValue(field, item),
																			});
																		} else {
																			refreshItem({ id: item?.id }, null, {
																				silent: true,
																			});
																		}
																	},
																);
															}
														}}
													/>
												) : field?.type === 'category' ? (
													<ReactSelect
														unstyled
														backspaceRemovesValue={false}
														styles={{
															control: (provided) => {
																return {
																	...provided,
																	outline: '',
																	cursor: 'pointer',
																};
															},
															option: (provided, state) => {
																return {
																	...provided,
																	paddingLeft: `${
																		(state.data.depth || 0) * 16 + 10
																	}px`,
																};
															},
														}}
														classNames={{
															control: ({ isFocused }) =>
																classNames(
																	isFocused &&
																		'!outline-none !-outline-offset-2 !outline-indigo-500 ',
																	'px-0 sm:px-3 outline-1 focus:outline-none focus:-outline-offset-2 hover:-outline-offset-1 outline-none -outline-offset-2 hover:outline-gray-400 focus:outline-indigo-500',
																),
															input: ({ isFocused }) => '',
															valueContainer: () => '',
															placeholder: () => 'py-2.5',
															menu: () =>
																'shadow-md border border-gray-100 bg-white',
															option: ({ isFocused, isSelected, isGroup }) =>
																classNames(
																	isFocused &&
																		'bg-gray-100 active:bg-gray-200 border-l-4 !border-indigo-500',
																	isSelected &&
																		'hover:border-indigo-500 !border-indigo-500 ',
																	'hover:cursor-pointer px-0 sm:px-3 py-2 border-l-4 border-white active:border-gray-100 hover:border-l-4 hover:border-indigo-500',
																),
															noOptionsMessage: () => 'py-4',
														}}
														placeholder="-"
														noOptionsMessage={() => 'No category found'}
														components={{
															ClearIndicator,

															...(value
																? { DropdownIndicator: () => null }
																: { DropdownIndicator }),
															MenuList: (props) => (
																<CustomMenuList
																	{...props}
																	customAction={{
																		label: 'Add a new category',
																		onClick: () =>
																			openNewCategory({
																				moduleId: ASSETS_MODULE_ID,
																				onSubmit: (newCategory) =>
																					onUpdateAsset(
																						{
																							id: item?.id,
																							category:
																								newCategory?.value || null,
																						},
																						(payload) => {
																							refreshItem(
																								{ id: item?.id },
																								null,
																								{
																									silent: true,
																								},
																							);
																							refreshAllAssets(null, null, {
																								silent: true,
																							});
																						},
																					),
																			}),
																	}}
																/>
															),
														}}
														value={categoryOptions?.find(
															(o) => o.value === value,
														)}
														isDisabled={isLoadingCategories === 'pending'}
														options={categoryOptions}
														onChange={(newValue) => {
															onUpdateAsset(
																{
																	id: item?.id,
																	category: newValue?.value || null,
																},
																(payload) => {
																	refreshItem({ id: item?.id }, null, {
																		silent: true,
																	});
																},
															);
														}}
														isClearable
													/>
												) : field?.type === 'location' ? (
													<div className="relative w-full">
														<div
															//contentEditable
															className={classNames(
																'w-full pr-10 outline-1 focus:outline-none focus:-outline-offset-2 hover:-outline-offset-1 outline-none -outline-offset-2 hover:outline-gray-400 focus:outline-indigo-500 py-2.5 px-0 sm:px-3 h-full w-full',
															)}
															onClick={() =>
																openSelectLocation({
																	locations,
																	onSelect: (selected) => {
																		onUpdateAsset(
																			{
																				id: item?.id,
																				location: selected?.id,
																			},
																			(payload, err) => {
																				refreshItem({ id: item?.id }, null, {
																					silent: true,
																				});
																				refreshAllAssets(null, null, {
																					silent: true,
																				});
																			},
																		);
																	},
																	selected: value,
																})
															}
														>
															{findPathById(locationOptionsTree, value)
																?.map((o) => o.name)
																?.reverse()
																.map((name, index) => (
																	<span
																		className={
																			index === 0
																				? 'text-black'
																				: 'text-gray-400 ml-3'
																		}
																	>
																		{name}
																	</span>
																)) || '-'}
														</div>
														{value && value !== '-' && (
															<XMarkIcon
																className="absolute right-3 top-0 mt-0 w-8 h-8 text-gray-500 px-2 py-1 cursor-pointer"
																onClick={() => {
																	onUpdateAsset(
																		{
																			id: item?.id,
																			location: null,
																		},
																		(payload, err) => {
																			refreshItem({ id: item?.id }, null, {
																				silent: true,
																			});
																			refreshAllAssets(null, null, {
																				silent: true,
																			});
																		},
																	);
																}}
															/>
														)}
													</div>
												) : null
											) : (
												<div className="py-2.5 px-0 sm:px-3">{value}</div>
											)}
										</div>
									</div>
								</li>
							);
						})}

						{reducedAttributes?.custom?.length && extended
							? reducedAttributes?.custom
									?.sort((a, b) =>
										a.label.toLowerCase().localeCompare(b.label.toLowerCase()),
									)
									?.map((field) => {
										const value = getValue(field);
										const isEditable = field?.isDefault
											? !['manufacturer', 'model'].includes(field?.key)
											: true;

										return (
											<li className="flex flex-wrap items-center justify-between gap-x-6 gap-y-4  sm:flex-nowrap text-xs">
												<div className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 flex-1">
													<div className="text-gray-500 pt-2">
														{field?.label}
													</div>
													<div className="">
														{isEditable
															? (() => {
																	switch (field?.type) {
																		case 'text':
																			return (
																				<input
																					placeholder="-"
																					defaultValue={value}
																					className={classNames(
																						'placeholder:text-black outline-1 focus:outline-none focus:-outline-offset-2 hover:-outline-offset-1 outline-none -outline-offset-2 hover:outline-gray-400 focus:outline-indigo-500 py-2.5 px-0 sm:px-3 h-full w-full',
																					)}
																					onBlur={(e) => {
																						onUpdateAsset(
																							{
																								id: item?.id,
																								customAttributes: [
																									{
																										fieldId: field?.id,
																										value: e.target.value,
																									},
																								],
																							},
																							(payload, err) => {
																								refreshItem(
																									{ id: item?.id },
																									null,
																									{
																										silent: true,
																									},
																								);
																							},
																						);
																					}}
																				/>
																			);
																		case 'textarea':
																			return (
																				<textarea
																					className={classNames(
																						'placeholder:text-black outline-1 focus:outline-none focus:-outline-offset-2 hover:-outline-offset-1 outline-none -outline-offset-2 hover:outline-gray-400 focus:outline-indigo-500 py-2.5 px-0 sm:px-3 h-full w-full',
																					)}
																					placeholder="-"
																					height="auto"
																					rows={3}
																					defaultValue={value}
																					onBlur={(e) => {
																						onUpdateAsset(
																							{
																								id: item?.id,
																								customAttributes: [
																									{
																										fieldId: field?.id,
																										value: e.target.value,
																									},
																								],
																							},
																							(payload, err) => {
																								refreshItem(
																									{ id: item?.id },
																									null,
																									{
																										silent: true,
																									},
																								);
																							},
																						);
																					}}
																				/>
																			);
																		case 'numeric':
																			return (
																				<input
																					placeholder="-"
																					defaultValue={value}
																					className={classNames(
																						'placeholder:text-black outline-1 focus:outline-none focus:-outline-offset-2 hover:-outline-offset-1 outline-none -outline-offset-2 hover:outline-gray-400 focus:outline-indigo-500 py-2.5 px-0 sm:px-3 h-full w-full',
																					)}
																					onBlur={(e) => {
																						onUpdateAsset(
																							{
																								id: item?.id,
																								customAttributes: [
																									{
																										fieldId: field?.id,
																										value: e.target.value,
																									},
																								],
																							},
																							(payload, err) => {
																								refreshItem(
																									{ id: item?.id },
																									null,
																									{
																										silent: true,
																									},
																								);
																							},
																						);
																					}}
																					type="number"
																					pattern="^-?[0-9]\d*\.?\d*$"
																				/>
																			);
																		case 'location':
																			return (
																				<div className="relative w-full">
																					<div
																						contentEditable
																						className={classNames(
																							'w-full pr-10 outline-1 focus:outline-none focus:-outline-offset-2 hover:-outline-offset-1 outline-none -outline-offset-2 hover:outline-gray-400 focus:outline-indigo-500 py-2.5 px-0 sm:px-3 h-full w-full',
																						)}
																						onFocus={() => {
																							openSelectLocation({
																								locations,
																								onSelect: (selected) => {
																									onUpdateAsset(
																										{
																											id: item?.id,
																											customAttributes: [
																												{
																													fieldId: field?.id,
																													value: selected?.id,
																												},
																											],
																										},
																										(payload, err) => {
																											refreshItem(
																												{ id: item?.id },
																												null,
																												{
																													silent: true,
																												},
																											);
																											refreshAllAssets(
																												null,
																												null,
																												{
																													silent: true,
																												},
																											);
																										},
																									);
																								},
																								selected: value,
																							});
																						}}
																					>
																						{findPathById(
																							locationOptionsTree,
																							value,
																						)
																							?.map((o) => o.name)
																							?.reverse()
																							.map((name, index) => (
																								<span
																									className={
																										index === 0
																											? 'text-black'
																											: 'text-gray-400 ml-3'
																									}
																								>
																									{name}
																								</span>
																							)) || '-'}
																					</div>
																					{value && value !== '-' && (
																						<XMarkIcon
																							className="absolute right-3 top-0 mt-0 w-8 h-8 text-gray-500 px-2 py-1 cursor-pointer"
																							onClick={() => {
																								onUpdateAsset(
																									{
																										id: item?.id,
																										customAttributes: [
																											{
																												fieldId: field?.id,
																												value: null,
																											},
																										],
																									},
																									(payload, err) => {
																										refreshItem(
																											{ id: item?.id },
																											null,
																											{
																												silent: true,
																											},
																										);
																										refreshAllAssets(
																											null,
																											null,
																											{
																												silent: true,
																											},
																										);
																									},
																								);
																							}}
																						/>
																					)}
																				</div>
																			);
																		case 'category':
																			return (
																				<ReactSelect
																					unstyled
																					backspaceRemovesValue={false}
																					styles={{
																						control: (provided) => {
																							return {
																								...provided,
																								outline: '',
																								cursor: 'pointer',
																							};
																						},
																						option: (provided, state) => {
																							return {
																								...provided,
																								paddingLeft: `${
																									(state.data.depth || 0) * 16 +
																									10
																								}px`,
																							};
																						},
																					}}
																					classNames={{
																						control: ({ isFocused }) =>
																							classNames(
																								isFocused &&
																									'!outline-none !-outline-offset-2 !outline-indigo-500 ',
																								'px-0 sm:px-3 outline-1 focus:outline-none focus:-outline-offset-2 hover:-outline-offset-1 outline-none -outline-offset-2 hover:outline-gray-400 focus:outline-indigo-500',
																							),
																						input: ({ isFocused }) => '',
																						valueContainer: () => '',
																						placeholder: () => 'py-2.5',
																						menu: () =>
																							'shadow-md border border-gray-100 bg-white',
																						option: ({
																							isFocused,
																							isSelected,
																							isGroup,
																						}) =>
																							classNames(
																								isFocused &&
																									'bg-gray-100 active:bg-gray-200 border-l-4 !border-indigo-500',
																								isSelected &&
																									'hover:border-indigo-500 !border-indigo-500 ',
																								'hover:cursor-pointer px-0 sm:px-3 py-2 border-l-4 border-white active:border-gray-100 hover:border-l-4 hover:border-indigo-500',
																							),
																						noOptionsMessage: () => 'py-4',
																					}}
																					placeholder="-"
																					noOptionsMessage={() =>
																						'No category found'
																					}
																					components={{
																						ClearIndicator,

																						...(value
																							? {
																									DropdownIndicator: () => null,
																								}
																							: { DropdownIndicator }),
																						MenuList: (props) => (
																							<CustomMenuList
																								{...props}
																								customAction={{
																									label: 'Add a new category',
																									onClick: () =>
																										openNewCategory({
																											moduleId:
																												ASSETS_MODULE_ID,
																											onSubmit: (newCategory) =>
																												onUpdateAsset(
																													{
																														id: item?.id,
																														customAttributes: [
																															{
																																fieldId:
																																	field?.id,
																																value:
																																	newCategory?.value,
																															},
																														],
																													},
																													(payload) => {
																														refreshItem(
																															{ id: item?.id },
																															null,
																															{
																																silent: true,
																															},
																														);
																													},
																												),
																										}),
																								}}
																							/>
																						),
																					}}
																					value={categoryOptions?.find(
																						(o) => o.value === value,
																					)}
																					isDisabled={
																						isLoadingCategories === 'pending'
																					}
																					options={categoryOptions}
																					onChange={(newValue) => {
																						onUpdateAsset(
																							{
																								id: item?.id,
																								customAttributes: [
																									{
																										fieldId: field?.id,
																										value:
																											newValue?.value || null,
																									},
																								],
																							},
																							(payload) => {
																								refreshItem(
																									{ id: item?.id },
																									null,
																									{
																										silent: true,
																									},
																								);
																								refreshAllAssets(null, null, {
																									silent: true,
																								});
																							},
																						);
																					}}
																					isClearable
																				/>
																			);
																		case 'boolean':
																			return (
																				<ReactSelect
																					unstyled
																					backspaceRemovesValue={false}
																					styles={{
																						control: (provided) => {
																							return {
																								...provided,
																								outline: '',
																								cursor: 'pointer',
																							};
																						},
																						option: (provided, state) => {
																							return {
																								...provided,
																								paddingLeft: `${
																									(state.data.depth || 0) * 16 +
																									10
																								}px`,
																							};
																						},
																					}}
																					classNames={{
																						control: ({ isFocused }) =>
																							classNames(
																								isFocused &&
																									'!outline-none !-outline-offset-2 !outline-indigo-500 ',
																								'px-0 sm:px-3 outline-1 focus:outline-none focus:-outline-offset-2 hover:-outline-offset-1 outline-none -outline-offset-2 hover:outline-gray-400 focus:outline-indigo-500',
																							),
																						input: ({ isFocused }) => '',
																						valueContainer: () => '',
																						placeholder: () => 'py-2.5',
																						menu: () =>
																							'shadow-md border border-gray-100 bg-white',
																						option: ({
																							isFocused,
																							isSelected,
																							isGroup,
																						}) =>
																							classNames(
																								isFocused &&
																									'bg-gray-100 active:bg-gray-200 border-l-4 !border-indigo-500',
																								isSelected &&
																									'hover:border-indigo-500 !border-indigo-500 ',
																								'hover:cursor-pointer px-0 sm:px-3 py-2 border-l-4 border-white active:border-gray-100 hover:border-l-4 hover:border-indigo-500',
																							),
																						noOptionsMessage: () => 'py-4',
																					}}
																					placeholder="-"
																					isSearchable={false}
																					components={{
																						ClearIndicator,

																						...(value
																							? {
																									DropdownIndicator: () => null,
																								}
																							: { DropdownIndicator }),
																					}}
																					value={booleanOptions?.find(
																						(o) => o.value === value,
																					)}
																					options={booleanOptions}
																					onChange={(newValue) => {
																						onUpdateAsset(
																							{
																								id: item?.id,
																								customAttributes: [
																									{
																										fieldId: field?.id,
																										value:
																											newValue?.value || null,
																									},
																								],
																							},
																							(payload) => {
																								refreshItem(
																									{ id: item?.id },
																									null,
																									{
																										silent: true,
																									},
																								);
																							},
																						);
																					}}
																					isClearable
																				/>
																			);
																		case 'vendor':
																			return (
																				<ReactSelect
																					unstyled
																					backspaceRemovesValue={false}
																					styles={{
																						control: (provided) => {
																							return {
																								...provided,
																								outline: '',
																								cursor: 'pointer',
																							};
																						},
																						option: (provided, state) => {
																							return {
																								...provided,
																								paddingLeft: `${
																									(state.data.depth || 0) * 16 +
																									10
																								}px`,
																							};
																						},
																					}}
																					classNames={{
																						control: ({ isFocused }) =>
																							classNames(
																								isFocused &&
																									'!outline-none !-outline-offset-2 !outline-indigo-500 ',
																								'px-0 sm:px-3 outline-1 focus:outline-none focus:-outline-offset-2 hover:-outline-offset-1 outline-none -outline-offset-2 hover:outline-gray-400 focus:outline-indigo-500',
																							),
																						input: ({ isFocused }) => '',
																						valueContainer: () => '',
																						placeholder: () => 'py-2.5',
																						menu: () =>
																							'shadow-md border border-gray-100 bg-white',
																						option: ({
																							isFocused,
																							isSelected,
																							isGroup,
																						}) =>
																							classNames(
																								isFocused &&
																									'bg-gray-100 active:bg-gray-200 border-l-4 !border-indigo-500',
																								isSelected &&
																									'hover:border-indigo-500 !border-indigo-500 ',
																								'hover:cursor-pointer px-0 sm:px-3 py-2 border-l-4 border-white active:border-gray-100 hover:border-l-4 hover:border-indigo-500',
																							),
																						noOptionsMessage: () => 'py-4',
																					}}
																					placeholder="-"
																					noOptionsMessage={() => 'Not found'}
																					components={{
																						ClearIndicator,

																						...(value
																							? {
																									DropdownIndicator: () => null,
																								}
																							: { DropdownIndicator }),
																						MenuList: (props) => (
																							<CustomMenuList
																								{...props}
																								customAction={{
																									label: 'Add a company',
																									onClick: () =>
																										openNewVendor({
																											onSubmit: (vendor) => {
																												onUpdateAsset(
																													{
																														id: item?.id,
																														customAttributes: [
																															{
																																fieldId:
																																	field?.id,
																																value:
																																	vendor?.value,
																															},
																														],
																													},
																													(payload) => {
																														refreshItem(
																															{ id: item?.id },
																															null,
																															{
																																silent: true,
																															},
																														);
																													},
																												);
																											},
																										}),
																								}}
																							/>
																						),
																					}}
																					value={vendorOptions?.find(
																						(o) => o.value === value,
																					)}
																					isDisabled={
																						isLoadingVendors === 'pending'
																					}
																					options={vendorOptions}
																					onChange={(newValue) => {
																						onUpdateAsset(
																							{
																								id: item?.id,
																								customAttributes: [
																									{
																										fieldId: field?.id,
																										value:
																											newValue?.value || null,
																									},
																								],
																							},
																							(payload) => {
																								refreshItem(
																									{ id: item?.id },
																									null,
																									{
																										silent: true,
																									},
																								);
																								refreshAllAssets(null, null, {
																									silent: true,
																								});
																							},
																						);
																					}}
																					isClearable
																				/>
																			);
																	}
																})()
															: null}
													</div>
												</div>
											</li>
										);
									})
							: null}

						<li className="">
							<div className="text-gray-500 pt-2">Tags</div>
							<div className="">
								<div className="text-sm text-gray-400 mt-1.5 font-light flex items-center flex-wrap wrap">
									{item?.tags?.map((tag) => (
										<Badge
											isBeta
											size="xxs"
											className={classNames(
												TAG_COLOR_OPTIONS.find(
													(color) => color?.key === tag?.color,
												)?.value,
												'mr-1.5 mt-1.5 cursor-pointer',
											)}
											onClick={() => navigate(`/assets/tags/${tag?.id}`)}
										>
											{tag?.name}
											<XMarkIcon
												className="w-6 h-6 pl-2 text-gray-600"
												onClick={(e) => {
													e.preventDefault();
													e.stopPropagation();

													onUpdateAsset(
														{
															id: item?.id,
															tagToRemove: tag?.id,
														},
														() => {
															refreshItem({ id: item?.id }, null, {
																silent: true,
															});
														},
													);
												}}
											/>
										</Badge>
									))}

									<Menu as="div" className="relative inline-block text-left">
										<div>
											<Menu.Button className="">
												<Badge
													isBeta
													size="xxs"
													className={classNames(
														'bg-gray-100 text-gray-800 ring-gray-300 mr-1.5 mt-1.5 cursor-pointer px-2 py-1.5',
													)}
													onClick={() => {}}
												>
													<PlusIcon className="w-3 h-3 text-gray-600" />
												</Badge>
											</Menu.Button>
										</div>

										<Menu.Items
											transition
											className="absolute left-0 z-50 mt-2 w-56 origin-top-right rounded-md bg-white shadow-lg ring-1 ring-black/5 transition focus:outline-none data-[closed]:scale-95 data-[closed]:transform data-[closed]:opacity-0 data-[enter]:duration-100 data-[leave]:duration-75 data-[enter]:ease-out data-[leave]:ease-in"
										>
											<div className="px-3 py-2 flex items-center">
												<div className="w-full">
													<TemplateReactSelect
														placeholder="Select a tag to add"
														options={tags?.map((tag) => ({
															label: tag?.name,
															value: tag?.id,
															color: tag?.color,
														}))}
														skip={item?.tags?.map((tag) => tag.id)}
														blurInputOnSelect
														onChange={(index, selected) => {
															onUpdateAsset(
																{
																	id: item?.id,
																	tagToAdd: selected?.value,
																},
																() => {
																	refreshItem({ id: item?.id }, null, {
																		silent: true,
																	});
																},
															);
														}}
													/>
												</div>

												<PlusCircleIcon
													className="w-10 h-10 py-2 text-gray-600 ml-1 cursor-pointer"
													onClick={() =>
														openTagModal({
															onSubmit: (selected) => {
																onUpdateAsset(
																	{
																		id: item?.id,
																		tagToAdd: selected?.value,
																	},
																	() => {
																		refreshItem({ id: item?.id }, null, {
																			silent: true,
																		});
																	},
																);
															},
														})
													}
												/>
											</div>
										</Menu.Items>
									</Menu>
								</div>
							</div>
						</li>
					</ul>
					<div className="hidden sm:block mb-6 mt-3 sm:mb-0 ">
						{reducedAttributes?.custom?.length ? (
							<>
								<a
									className="text-indigo-800 hover:text-indigo-600 text-xs cursor-pointer mr-1.5"
									onClick={() => {
										updateSettings(
											{
												type: 'user',
												key: 'extendedAssetAttributes',
												value: !extended,
											},
											{ showNotification: false },
										);
										setExtended(!extended);
									}}
								>
									{extended
										? 'Hide all custom fields'
										: `Show (${reducedAttributes?.custom?.length}) fields`}
								</a>
								<span className="text-gray-300 mr-1.5">-</span>
							</>
						) : null}
						<a
							className="text-indigo-800 hover:text-indigo-600 text-xs cursor-pointer"
							onClick={() => {
								openManageAttributes({
									moduleId: ASSETS_MODULE_ID,
									onSelect: (id, active) => {
										console.log('select:', id, active);
									},
								});
							}}
						>
							Manage fields
						</a>
					</div>

					{/* Service Vendors Section */}
					<div className="pt-6">
						<div className="flex items-center justify-between mb-4">
							<h3 className="text-sm font-medium text-gray-900 flex items-center">
								{' '}
								Service Vendors
							</h3>
							<Button
								type="ghost"
								size="sm"
								onClick={() =>
									openVendorPicker({
										title: 'Select Vendor',
										skip: item?.contacts?.map((c) => c.id) || [],
										onSelect: (vendors) => {
											onAddContacts(
												{
													id: item?.id,
													contacts: vendors.map((v) => v.id),
												},
												() => {
													refreshItem({ id: item?.id }, null, {
														silent: true,
													});
												},
											);
										},
										showCreateButton: true,
										onCreateNew: () => {
											openNewVendor({
												onSubmit: (vendor) => {
													onAddContacts(
														{
															id: item?.id,
															contacts: [vendor.value],
														},
														() => {
															refreshItem({ id: item?.id }, null, {
																silent: true,
															});
														},
													);
												},
											});
										},
									})
								}
							>
								Add vendor
							</Button>
						</div>
						{item?.contacts?.length > 0 ? (
							<ul className="divide-y divide-gray-100 rounded-md border border-gray-200">
								{item.contacts.map((contact) => (
									<li
										key={contact.id}
										className="flex items-center justify-between py-3 pl-4 pr-4 text-xs hover:bg-gray-50 cursor-pointer"
										onClick={() => onViewContact({ id: contact?.id })}
									>
										<div className="flex items-center min-w-0 flex-1">
											<div className="h-8 w-8 rounded-full bg-gray-200 flex items-center justify-center mr-3">
												<UsersIcon className="h-4 w-4 text-gray-500" />
											</div>
											<div className="min-w-0 flex-1">
												<p className="truncate font-medium text-gray-900">
													{contact?.type === 'person'
														? `${contact.firstName} ${contact.lastName}`
														: contact.companyName}
												</p>
												<p className="text-xs text-gray-500">
													{contact?.type === 'person' && contact?.title && (
														<span>
															{contact.title}
															{contact.companyName
																? ` at ${contact.companyName}`
																: ''}
														</span>
													)}
													{contact?.email && (
														<span className="ml-2">{contact.email}</span>
													)}
												</p>
											</div>
										</div>
										<ChevronRightIcon className="h-4 w-4 text-gray-400" />
									</li>
								))}
							</ul>
						) : (
							<div className="text-center py-6 border border-dashed border-gray-300 rounded-md">
								<UsersIcon className="mx-auto h-8 w-8 text-gray-400" />
								<p className="mt-2 text-sm text-gray-500">
									No vendors attached
								</p>
								<Button
									type="secondary"
									size="sm"
									className="mt-3"
									onClick={() =>
										openVendorPicker({
											title: 'Select Vendor',
											skip: item?.contacts?.map((c) => c.id) || [],
											onSelect: (vendors) => {
												onAddContacts(
													{
														id: item?.id,
														contacts: vendors.map((v) => v.id),
													},
													() => {
														refreshItem({ id: item?.id }, null, {
															silent: true,
														});
													},
												);
											},
											showCreateButton: true,
											onCreateNew: () => {
												openNewVendor({
													onSubmit: (vendor) => {
														onAddContacts(
															{
																id: item?.id,
																contacts: [vendor.value],
															},
															() => {
																refreshItem({ id: item?.id }, null, {
																	silent: true,
																});
															},
														);
													},
												});
											},
										})
									}
								>
									Add Vendor
								</Button>
							</div>
						)}
					</div>
				</div>
				<div className="col-span-3">
					{/* Open Tickets Section */}
					<div className="bg-white border-gray-200 border rounded-lg">
						<div className="flex items-center justify-between py-4 px-6 border-b border-gray-200">
							<h3 className="text-sm font-medium text-gray-900 flex items-center">
								Open Tickets
							</h3>
							<div className="flex items-center space-x-2">
								<Button
									type="ghost"
									size="sm"
									onClick={() =>
										navigate(`/tickets-beta?assetId=${item?.id}&status=all`)
									}
								>
									View All
								</Button>
								<Button
									type="secondary"
									size="sm"
									onClick={() =>
										openCreateTicket({
											assetId: item?.id,
											asset: item,
											onSuccess: () => {
												refreshTickets({ assetId: item?.id });
											},
										})
									}
								>
									Report Issue
								</Button>
							</div>
						</div>
						{tickets?.rows?.filter(
							(t) => !['closed', 'done', 'resolved'].includes(t.status),
						)?.length > 0 ? (
							<ul className="divide-y divide-gray-100 rounded-md">
								{tickets?.rows
									?.filter(
										(t) => !['closed', 'done', 'resolved'].includes(t.status),
									)
									?.slice(0, 5)
									.map((ticket) => (
										<li
											key={ticket.id}
											className="flex items-center justify-between py-3 px-6 text-xs hover:bg-gray-50 cursor-pointer"
											onClick={() => onOpenTicket({ id: ticket?.id })}
										>
											<div className="flex items-center min-w-0 flex-1">
												<div className="min-w-0 flex-1">
													<p className="truncate font-medium text-gray-900">
														{ticket?.subject || 'Untitled'}
													</p>
													<p className="text-xs text-gray-500">
														{ticket?.createdBy && (
															<span className="mr-1">
																{`Reported by: ${ticket.createdBy.firstName} ${ticket.createdBy.lastName}`}
															</span>
														)}
														{ticket?.createdAt && (
															<>
																<span className="mr-1">•</span>
																<span className="mr-1">
																	{moment(ticket.createdAt).fromNow()}
																</span>
																<span className="text-gray-400">
																	(
																	{moment(ticket.createdAt).format(
																		'MMM D, YYYY',
																	)}
																	)
																</span>
															</>
														)}
													</p>
												</div>
											</div>
											<div className="flex items-center">
												<StatusBadge
													status={ticket?.status}
													size="xs"
													className="mr-3"
												/>
												<ChevronRightIcon className="h-4 w-4 text-gray-400" />
											</div>
										</li>
									))}
							</ul>
						) : (
							<div className="text-center py-6 rounded-md">
								<p className="py-4 text-sm text-gray-500">
									No open tickets.{' '}
									<a
										href={`/work-orders?assetId=${item?.id}&status=all`}
										onClick={(e) => {
											e.preventDefault();
											navigate(`/tickets-beta?assetId=${item?.id}&status=all`);
										}}
										className="text-sm text-indigo-600 hover:text-indigo-500 cursor-pointer"
									>
										View past
									</a>
								</p>
							</div>
						)}
					</div>

					{/* Work Orders Section */}
					<div className="bg-white border-gray-200 border rounded-lg mt-6">
						<div className="flex items-center justify-between py-4 px-6 border-b border-gray-200">
							<h3 className="text-sm font-medium text-gray-900 flex items-center">
								Active Work Orders
							</h3>
							<div className="flex items-center space-x-2">
								<Button
									type="ghost"
									size="sm"
									onClick={() =>
										navigate(`/work-orders?assetId=${item?.id}&status=all`)
									}
								>
									View All
								</Button>
								<Button
									type="secondary"
									size="sm"
									onClick={() =>
										openCreateWorkOrder({
											assetId: item?.id,
											asset: item,
											onSuccess: () => {
												refreshWorkOrders({ assetId: item?.id });
											},
										})
									}
								>
									Create Work Order
								</Button>
							</div>
						</div>
						{workOrders?.rows?.filter(
							(wo) => !['completed', 'cancelled'].includes(wo.status),
						)?.length > 0 ? (
							<ul className="divide-y divide-gray-100 rounded-md m-0 p-0 !mt-0">
								{workOrders?.rows
									?.filter(
										(wo) => !['completed', 'cancelled'].includes(wo.status),
									)
									?.slice(0, 5)
									.map((workOrder) => {
										const statusColors = {
											draft: 'default',
											pending: 'warning',
											in_progress: 'info',
											completed: 'success',
											cancelled: 'error',
										};
										return (
											<li
												key={workOrder.id}
												className="flex items-center justify-between py-3 px-6 text-xs hover:bg-gray-50 cursor-pointer"
												onClick={() => onOpenWorkOrder({ id: workOrder?.id })}
											>
												<div className="flex items-center min-w-0 flex-1">
													<div className="min-w-0 flex-1">
														<p className="truncate font-medium text-gray-900">
															{workOrder?.title || 'Untitled'}
														</p>
														<p className="text-xs text-gray-500">
															{workOrder?.dueDate && (
																<span className="">
																	Due{' '}
																	{moment(workOrder.dueDate).format(
																		'MMM D, YYYY',
																	)}
																</span>
															)}
														</p>
													</div>
												</div>
												<div className="flex items-center">
													<StatusBadge
														size="xs"
														status={workOrder?.status}
														className="mr-3"
													/>
													<ChevronRightIcon className="h-4 w-4 text-gray-400" />
												</div>
											</li>
										);
									})}
							</ul>
						) : (
							<div className="text-center py-6 rounded-md">
								<p className="py-4 text-sm text-gray-500">
									No active work orders.{' '}
									<a
										href={`/work-orders?assetId=${item?.id}&status=all`}
										onClick={(e) => {
											e.preventDefault();
											navigate(`/work-orders?assetId=${item?.id}&status=all`);
										}}
										className="text-sm text-indigo-600 hover:text-indigo-500 cursor-pointer"
									>
										View past
									</a>
								</p>
							</div>
						)}
					</div>

					{/* PM Schedules Section */}
					<div className="bg-white border-gray-200 border rounded-lg mt-6">
						<div className="flex items-center justify-between py-4 px-6 border-b border-gray-200">
							<h3 className="text-sm font-medium text-gray-900 flex items-center">
								<ClockIcon className="w-4 h-4 mr-2 text-gray-500" />
								PM Schedules
							</h3>
							<Button
								type="secondary"
								size="sm"
								onClick={() =>
									openEditPMSchedule({
										assetId: item?.id,
										onSuccess: () => {
											refreshPMSchedules({ assetId: item?.id });
										},
									})
								}
							>
								Add PM Schedule
							</Button>
						</div>
						{pmSchedules?.rows?.filter((schedule) =>
							schedule?.directAssets?.some((asset) => asset.id === item?.id),
						)?.length > 0 ? (
							<ul className="divide-y divide-gray-100 rounded-md m-0 p-0 !mt-0">
								{pmSchedules?.rows
									?.filter((schedule) =>
										schedule?.directAssets?.some((asset) => asset.id === item?.id),
									)
									?.slice(0, 5)
									.map((schedule) => {
									// Calculate next due date
									const nextDue = schedule?.nextDueDate
										? moment(schedule.nextDueDate)
										: null;
									const isOverdue =
										nextDue && nextDue.isBefore(moment(), 'day');

									return (
										<li
											key={schedule.id}
											className="flex items-center justify-between py-3 pl-4 pr-4 text-xs hover:bg-gray-50"
										>
											<div
												className="flex items-center min-w-0 flex-1 cursor-pointer"
												onClick={() => onOpenPMSchedule({ scheduleId: schedule?.id })}
											>
												<ClockIcon
													className={classNames(
														'h-4 w-4 flex-shrink-0 mr-3',
														isOverdue
															? 'text-red-500'
															: schedule?.active
																? 'text-blue-500'
																: 'text-gray-400',
													)}
												/>
												<div className="min-w-0 flex-1">
													<p className="truncate font-medium text-gray-900">
														{schedule?.name || 'Untitled Schedule'}
													</p>
													<p className="text-xs text-gray-500">
														{schedule?.frequency && (
															<span className="mr-2 capitalize">
																{schedule.frequency.replace('_', ' ')}
															</span>
														)}
														{nextDue && (
															<span
																className={classNames(
																	isOverdue ? 'text-red-600' : '',
																)}
															>
																{isOverdue ? 'Overdue: ' : 'Next: '}
																{nextDue.format('MMM D, YYYY')}
															</span>
														)}
													</p>
												</div>
											</div>
											<div className="flex items-center gap-2">
												<StatusBadge
													size="xs"
													status={schedule?.active ? 'active' : 'inactive'}
												/>
												<Menu as="div" className="relative">
													<Menu.Button className="p-1 hover:bg-gray-100 rounded-full">
														<EllipsisVerticalIcon className="h-4 w-4 text-gray-400" />
													</Menu.Button>
													<Menu.Items className="absolute right-0 z-10 mt-2 w-48 origin-top-right rounded-lg bg-white shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none">
														<div className="py-1">
															<Menu.Item>
																{({ active }) => (
																	<button
																		onClick={() =>
																			onOpenPMSchedule({ scheduleId: schedule?.id })
																		}
																		className={classNames(
																			active ? 'bg-gray-100' : '',
																			'block w-full text-left px-4 py-2 text-sm text-gray-700',
																		)}
																	>
																		View Schedule
																	</button>
																)}
															</Menu.Item>
															<Menu.Item>
																{({ active }) => (
																	<button
																		onClick={() => {
																			onRemovePMSchedule(
																				{
																					id: schedule.id,
																					assetId: item?.id,
																				},
																				() => {
																					// Refresh PM schedules list after removal
																					refreshPMSchedules({
																						assetId: item?.id,
																					});
																				},
																			);
																		}}
																		className={classNames(
																			active ? 'bg-gray-100' : '',
																			'block w-full text-left px-4 py-2 text-sm text-red-600',
																		)}
																	>
																		Remove from Asset
																	</button>
																)}
															</Menu.Item>
														</div>
													</Menu.Items>
												</Menu>
											</div>
										</li>
									);
								})}
							</ul>
						) : (
							<div className="text-center py-6 rounded-md">
								<p className="py-4 text-sm text-gray-500">
									No PM schedules assigned to this asset.{' '}
									<a
										href={`/pm-schedules`}
										onClick={(e) => {
											e.preventDefault();
											navigate(`/pm-schedules`);
										}}
										className="text-sm text-indigo-600 hover:text-indigo-500 cursor-pointer"
									>
										View all schedules
									</a>
								</p>
							</div>
						)}
					</div>

					{/* Open Tasks Section */}
					{/*<div className="bg-white border-gray-200 border rounded-lg mt-6">
						<div className="flex items-center justify-between py-4 px-6 border-b border-gray-200">
							<h3 className="text-sm font-medium text-gray-900 flex items-center">
								Open Tasks
							</h3>
							<Button
								type="ghost"
								size="sm"
								onClick={() => navigate(`/tasks?assetId=${item?.id}`)}
							>
								View All
							</Button>
						</div>
						{tasks?.rows?.length > 0 ? (
							<ul className="divide-y divide-gray-100 rounded-md m-0 p-0 !mt-0">
								{' '}
								{tasks?.rows?.slice(0, 5).map((task) => {
									const dueDate = task.dueDate;
									const dueDateMoment = dueDate ? moment.utc(dueDate) : null;
									const todayStart = moment().startOf('day');
									const isOverdue =
										dueDateMoment &&
										!task.completed &&
										dueDateMoment.isBefore(todayStart);

									return (
										<li
											key={task.id}
											className="flex items-center gap-3 py-3 pl-4 pr-4 text-sm hover:bg-gray-50 cursor-pointer"
											onClick={() => onOpenTask({ id: task?.id })}
										>
											<button
												type="button"
												onClick={(e) => {
													e.stopPropagation();
													handleToggleTask(task.id, task.completed);
												}}
												className="flex-shrink-0"
											>
												{task.completed ? (
													<CheckCircleSolidIcon className="h-5 w-5 text-green-500" />
												) : (
													<div className="h-5 w-5 rounded-full border-2 border-gray-300 hover:border-indigo-500 transition-colors" />
												)}
											</button>

											<div className="flex-1 min-w-0">
												<p
													className={classNames(
														'text-sm font-medium truncate',
														task.completed ? 'text-gray-400' : 'text-gray-900',
													)}
												>
													{task?.title || 'Untitled'}
												</p>
												<div className="flex items-center gap-3 mt-1">
													{task?.dueDate && (
														<span
															className={classNames(
																'text-xs flex items-center gap-1',
																isOverdue
																	? 'text-red-600 font-medium'
																	: 'text-gray-500',
															)}
														>
															<ClockIcon className="h-3 w-3" />
															{moment(task.dueDate).format('MMM D')}
														</span>
													)}
												</div>
											</div>

											{task.assignedToUser && (
												<div className="flex-shrink-0">
													<AvatarGenerated
														id={getUserFullName(task.assignedToUser)}
														size="xs"
														title={getUserFullName(task.assignedToUser)}
													/>
												</div>
											)}

											<StatusBadge
												status={
													task.skipped === true
														? 'skipped'
														: task.completed === true
															? 'completed'
															: 'open'
												}
											/>
										</li>
									);
								})}
							</ul>
						) : (
							<div className="text-center py-6 border border-dashed border-gray-300 rounded-md">
								<ClipboardDocumentListIcon className="mx-auto h-8 w-8 text-gray-400" />
								<p className="mt-2 text-sm text-gray-500">No open tasks</p>
							</div>
						)}
					</div> */}

					{/* Contracts Section */}
					<div className="bg-white border-gray-200 border rounded-lg mt-6">
						<div className="flex items-center justify-between py-4 px-6 border-b border-gray-200">
							<h3 className="text-sm font-medium text-gray-900 flex items-center">
								<DocumentTextIcon className="w-4 h-4 mr-2 text-gray-500" />
								Contracts
							</h3>
							<Menu as="div" className="relative">
								<Menu.Button className="inline-flex items-center gap-2 px-3 py-1.5 text-xs font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500">
									<PlusIcon className="h-4 w-4" />
									Add Contract
									<ChevronDownIcon className="h-4 w-4" />
								</Menu.Button>
								<Menu.Items className="absolute right-0 z-10 mt-2 w-56 origin-top-right rounded-lg bg-white shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none">
									<div className="py-1">
										<Menu.Item>
											{({ active }) => (
												<button
													onClick={() =>
														openContractPicker({
															multiSelect: true,
															maxSelect: 10,
															skip: contracts?.rows?.map((c) => c.id) || [],
															onSelect: (selectedContracts) => {
																// Link each selected contract to the asset
																selectedContracts.forEach((contract) => {
																	onLinkContract(
																		{
																			id: contract.id,
																			assetId: item?.id,
																		},
																		() => {
																			// Refresh contracts list after linking
																			refreshContracts({ assetId: item?.id });
																		},
																	);
																});
															},
														})
													}
													className={classNames(
														active ? 'bg-gray-100' : '',
														'block w-full text-left px-4 py-2 text-sm text-gray-700',
													)}
												>
													Add Existing Contract
												</button>
											)}
										</Menu.Item>
										<Menu.Item>
											{({ active }) => (
												<button
													onClick={() =>
														openAddContract({
															assetId: item?.id,
															onSuccess: () => {
																refreshContracts({ assetId: item?.id });
															},
														})
													}
													className={classNames(
														active ? 'bg-gray-100' : '',
														'block w-full text-left px-4 py-2 text-sm text-gray-700',
													)}
												>
													Create New Contract
												</button>
											)}
										</Menu.Item>
									</div>
								</Menu.Items>
							</Menu>
						</div>
						{contracts?.rows?.length > 0 ? (
							<ul className="divide-y divide-gray-100 rounded-md m-0 p-0 !mt-0">
								{contracts?.rows?.map((contract) => {
									const now = moment();
									const endDate = moment(contract?.endDate);
									const isExpired = now.isAfter(endDate);
									const daysUntilExpiry = endDate.diff(now, 'days');
									const isExpiringSoon =
										!isExpired && daysUntilExpiry <= 30 && daysUntilExpiry >= 0;
									const isActive = contract?.active && !isExpired;
									return (
										<li
											key={contract.id}
											className="flex items-center justify-between py-3 pl-4 pr-4 text-xs hover:bg-gray-50"
										>
											<div
												className="flex items-center min-w-0 flex-1 cursor-pointer"
												onClick={() => onOpenContract({ id: contract?.id })}
											>
												<DocumentTextIcon
													className={classNames(
														'h-4 w-4 flex-shrink-0 mr-3',
														isActive
															? isExpiringSoon
																? 'text-orange-500'
																: 'text-blue-500'
															: 'text-gray-400',
													)}
												/>
												<div className="min-w-0 flex-1">
													<p className="truncate font-medium text-gray-900">
														{contract?.name || 'Untitled'}
													</p>
													<p className="text-xs text-gray-500">
														{contract?.vendor?.companyName && (
															<span className="mr-2">
																{contract.vendor.companyName}
															</span>
														)}
														{contract?.endDate && (
															<span
																className={classNames(
																	isExpired
																		? 'text-red-600'
																		: isExpiringSoon
																			? 'text-orange-600'
																			: '',
																)}
															>
																{isExpired ? 'Expired' : 'Expires'}{' '}
																{moment(contract.endDate).format('MMM D, YYYY')}
																{isExpiringSoon && ` (${daysUntilExpiry} days)`}
															</span>
														)}
													</p>
												</div>
											</div>
											<div className="flex items-center gap-2">
												<StatusBadge
													size="xs"
													status={
														isExpired
															? 'Expired'
															: isExpiringSoon
																? 'Expiring Soon'
																: isActive
																	? 'Active'
																	: 'Inactive'
													}
												/>
												<Menu as="div" className="relative">
													<Menu.Button className="p-1 hover:bg-gray-100 rounded-full">
														<EllipsisVerticalIcon className="h-4 w-4 text-gray-400" />
													</Menu.Button>
													<Menu.Items className="absolute right-0 z-10 mt-2 w-48 origin-top-right rounded-lg bg-white shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none">
														<div className="py-1">
															<Menu.Item>
																{({ active }) => (
																	<button
																		onClick={() =>
																			onOpenContract({ id: contract?.id })
																		}
																		className={classNames(
																			active ? 'bg-gray-100' : '',
																			'block w-full text-left px-4 py-2 text-sm text-gray-700',
																		)}
																	>
																		View Contract
																	</button>
																)}
															</Menu.Item>
															<Menu.Item>
																{({ active }) => (
																	<button
																		onClick={() => {
																			onRemoveContract(
																				{
																					id: contract.id,
																					assetId: item?.id,
																				},
																				() => {
																					refreshContracts({
																						assetId: item?.id,
																					});
																				},
																			);
																		}}
																		className={classNames(
																			active ? 'bg-gray-100' : '',
																			'block w-full text-left px-4 py-2 text-sm text-red-600',
																		)}
																	>
																		Remove from Asset
																	</button>
																)}
															</Menu.Item>
														</div>
													</Menu.Items>
												</Menu>
											</div>
										</li>
									);
								})}
							</ul>
						) : (
							<div className="text-center py-6  rounded-md">
								<p className="mt-2 text-sm text-gray-500">No contracts found</p>
							</div>
						)}
					</div>

					{/* Documents Section */}
					<div className="bg-white border-gray-200 border rounded-lg mt-6">
						<div className="flex items-center justify-between py-4 px-6 border-b border-gray-200">
							<h3 className="text-sm font-medium text-gray-900 flex items-center">
								Documents
							</h3>
							<div className="flex items-center space-x-2">
								<FileUpload
									files={fileList}
									onChange={(files) => {
										// Check if files are from library or new uploads
										const libraryDocs = files.filter(
											(f) => f.id || f.fromLibrary,
										);
										const newUploads = files.filter(
											(f) => !f.id && !f.fromLibrary,
										);

										if (libraryDocs.length > 0) {
											// Add library documents directly to asset
											const documentIds = libraryDocs.map((f) => f.id);
											onAddDocuments(
												{
													id: item?.id,
													documents: documentIds,
												},
												() => {
													refreshDocuments({ assetId: item?.id, limit: 5 });
													setFileList([]);
												},
											);
										} else if (newUploads.length > 0) {
											// For new uploads, open the upload modal
											openUploadDocumentsModal({
												moduleId: DOCUMENTS_MODULE_ID,
												assetId: item?.id,
												onSubmit: () => {
													refreshDocuments({ assetId: item?.id, limit: 5 });
													setFileList([]);
												},
											});
										}
									}}
									multiple
									acceptTypes="image/jpeg image/png application/pdf"
									showLibraryOption
									buttonText="Add Documents"
									className="text-xs [&_[role='menu']_button]:min-w-[190px]"
								/>
							</div>
						</div>
						{documents?.rows?.length > 0 ? (
							<>
								<ul className="divide-y divide-gray-100 rounded-md m-0 p-0 !mt-0">
									{(() => {
										const pageSize = 5;
										const sortedDocs = [...(documents?.rows || [])].sort(
											(a, b) => new Date(b.createdAt) - new Date(a.createdAt),
										);
										const startIndex = (documentsPage - 1) * pageSize;
										const paginatedDocs = sortedDocs.slice(
											startIndex,
											startIndex + pageSize,
										);
										return paginatedDocs;
									})().map((doc) => (
										<li
											key={doc.id}
											className="flex items-center justify-between py-3 pl-4 pr-4 text-xs hover:bg-gray-50"
										>
											<div
												className="flex items-center min-w-0 flex-1 cursor-pointer"
												onClick={() => openDocumentPreviewWithEdit(doc.id)}
											>
												<PaperClipIcon className="h-4 w-4 flex-shrink-0 text-gray-400 mr-3" />
												<div className="min-w-0 flex-1">
													<p className="truncate font-medium text-gray-900">
														{doc?.name || 'Untitled'}
													</p>
													<p className="text-xs text-gray-500">
														{doc?.category?.name && (
															<span className="mr-2">{doc.category.name}</span>
														)}
														{doc?.createdAt &&
															moment(doc.createdAt).format('MMM D, YYYY')}
													</p>
												</div>
											</div>
											<Menu as="div" className="relative">
												<Menu.Button className="p-1 hover:bg-gray-100 rounded-full">
													<EllipsisVerticalIcon className="h-4 w-4 text-gray-400" />
												</Menu.Button>
												<Menu.Items className="absolute right-0 z-10 mt-2 w-48 origin-top-right rounded-lg bg-white shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none">
													<div className="py-1">
														<Menu.Item>
															{({ active }) => (
																<button
																	onClick={() => openDocumentPreviewWithEdit(doc.id)}
																	className={classNames(
																		active ? 'bg-gray-100' : '',
																		'block w-full text-left px-4 py-2 text-sm text-gray-700',
																	)}
																>
																	View Document
																</button>
															)}
														</Menu.Item>
														<Menu.Item>
															{({ active }) => (
																<button
																	onClick={() => {
																		onRemoveDocument(
																			{
																				id: item?.id,
																				documentId: doc.id,
																			},
																			() => {
																				refreshDocuments({ assetId: item?.id, limit: 5 });
																			},
																		);
																	}}
																	className={classNames(
																		active ? 'bg-gray-100' : '',
																		'block w-full text-left px-4 py-2 text-sm text-red-600',
																	)}
																>
																	Remove from Asset
																</button>
															)}
														</Menu.Item>
													</div>
												</Menu.Items>
											</Menu>
										</li>
									))}
								</ul>
								{/* Pagination */}
								{(() => {
									const pageSize = 5;
									const sortedDocs = [...(documents?.rows || [])].sort(
										(a, b) => new Date(b.createdAt) - new Date(a.createdAt),
									);
									const totalPages = Math.ceil(sortedDocs.length / pageSize);
									const startItem = (documentsPage - 1) * pageSize + 1;
									const endItem = Math.min(
										documentsPage * pageSize,
										sortedDocs.length,
									);

									if (totalPages <= 1) return null;

									return (
										<div className="flex items-center justify-between px-4 py-3 border-t border-gray-100">
											<span className="text-sm text-gray-500">
												Showing {startItem}-{endItem} of {sortedDocs.length}
											</span>
											<div className="flex items-center gap-2">
												<button
													onClick={() => setDocumentsPage(documentsPage - 1)}
													disabled={documentsPage === 1}
													className={`p-2 rounded-lg transition-colors ${
														documentsPage === 1
															? 'text-gray-300 cursor-not-allowed'
															: 'text-gray-500 hover:bg-gray-100 hover:text-gray-700'
													}`}
												>
													<ChevronLeftIcon className="w-5 h-5" />
												</button>
												<span className="text-sm text-gray-700 px-2">
													Page {documentsPage} of {totalPages}
												</span>
												<button
													onClick={() => setDocumentsPage(documentsPage + 1)}
													disabled={documentsPage === totalPages}
													className={`p-2 rounded-lg transition-colors ${
														documentsPage === totalPages
															? 'text-gray-300 cursor-not-allowed'
															: 'text-gray-500 hover:bg-gray-100 hover:text-gray-700'
													}`}
												>
													<ChevronRightIcon className="w-5 h-5" />
												</button>
											</div>
										</div>
									);
								})()}
							</>
						) : (
							<div className="text-center py-6 rounded-md">
								<p className="mt-2 text-sm text-gray-500">No documents found</p>
							</div>
						)}
					</div>

					{/* Asset Timeline Section */}

					{/*}
					{isSuperAdmin && (
						<div className="bg-white border-gray-200 border rounded-lg mt-6">
							<div className="flex items-center justify-between py-4 px-6 border-b border-gray-200">
								<h3 className="text-sm font-medium text-gray-900 flex items-center">
									Timeline
								</h3>
							</div>
							{timelineItems.length > 0 ? (
								<div className="flow-root">
									<ul className="-mb-8">
										{timelineItems.slice(0, 5).map((event, idx) => {
											const isLast =
												idx === Math.min(timelineItems.length, 5) - 1;

											const getIcon = () => {
												switch (event.type) {
													case 'created':
														return (
															<PlusCircleIcon className="h-4 w-4 text-green-500" />
														);
													case 'ticket':
														return (
															<TicketIcon
																className={classNames(
																	'h-4 w-4',
																	event.status === 'closed'
																		? 'text-green-500'
																		: 'text-orange-500',
																)}
															/>
														);
													case 'contract':
														return (
															<DocumentTextIcon
																className={classNames(
																	'h-4 w-4',
																	event.isExpired
																		? 'text-gray-400'
																		: 'text-blue-500',
																)}
															/>
														);
													case 'service':
														return (
															<WrenchScrewdriverIcon
																className={classNames(
																	'h-4 w-4',
																	event.status === 'COMPLETED'
																		? 'text-green-500'
																		: 'text-indigo-500',
																)}
															/>
														);
													default:
														return (
															<CheckCircleIcon className="h-4 w-4 text-gray-400" />
														);
												}
											};

											const handleClick = () => {
												switch (event.type) {
													case 'ticket':
														onOpenTicket({ id: event.id });
														break;
													case 'contract':
														onOpenContract({ id: event.id });
														break;
													case 'service':
														onOpenEvent({ id: event.id });
														break;
													default:
														break;
												}
											};

											return (
												<li key={`${event.type}-${event.id}`}>
													<div className="relative pb-6">
														{!isLast && (
															<span
																className="absolute left-3 top-4 -ml-px h-full w-0.5 bg-gray-200"
																aria-hidden="true"
															/>
														)}
														<div className="relative flex space-x-3">
															<div>
																<span className="h-6 w-6 rounded-full bg-gray-50 flex items-center justify-center ring-4 ring-white">
																	{getIcon()}
																</span>
															</div>
															<div
																className={classNames(
																	'flex min-w-0 flex-1 justify-between space-x-4 pt-0.5',
																	event.type !== 'created' &&
																		'cursor-pointer hover:bg-gray-50 -ml-1 pl-1 pr-2 rounded-md',
																)}
																onClick={
																	event.type !== 'created'
																		? handleClick
																		: undefined
																}
															>
																<div className="min-w-0 flex-1">
																	<p className="text-sm text-gray-900 truncate">
																		{event.title}
																		{event.subtitle && (
																			<span className="text-gray-500 ml-1 text-xs">
																				{event.subtitle}
																			</span>
																		)}
																	</p>
																</div>
																<div className="whitespace-nowrap text-right text-xs text-gray-500">
																	{moment(event.date).format('MMM D, YYYY')}
																</div>
															</div>
														</div>
													</div>
												</li>
											);
										})}
									</ul>
								</div>
							) : (
								<div className="text-center py-6 border border-dashed border-gray-300 rounded-md">
									<ClockIcon className="mx-auto h-8 w-8 text-gray-400" />
									<p className="mt-2 text-sm text-gray-500">
										No timeline events yet
									</p>
									<p className="text-xs text-gray-400">
										Events will appear as you add tickets, contracts, and
										services
									</p>
								</div>
							)}
						</div>
					)}

					*/}
				</div>
			</div>

			<div></div>

			{/*<div className="hidden sm:block">
				<WidgetDashboard
					widgetSettings={settings?.user?.widgetSettings}
					isLoading={isLoadingWidgets === 'pending' || isLoadingSettings}
					availableWidgets={getAvailableWidgets(
						'assetDashboard',
						widgets?.map((w) => w.key),
					)}
					activeWidgets={
						settings?.user?.widgets?.assetDashboard //|| ['asset-overview']
					}
					onChange={(state) => {
						if (
							settings?.user?.widgets?.assetDashboard?.length !==
								state?.length ||
							!isEqual(settings?.user?.widgets?.assetDashboard, state)
						) {
							updateSettings({
								type: 'user',
								key: 'widgets',
								value: {
									...settings?.user?.widgets,
									assetDashboard: state,
								},
							});
						}
					}}
				/>
			</div>*/}

			{/* 2-column grid for sections */}
			<div className="grid grid-cols-1 md:grid-cols-2 gap-6 mt-8"></div>
		</div>
	);
	return (
		<div className="my-10 grid grid-cols-4 gap-6 pb-20">
			<StatusChartWidget item={item} />
			<OverviewWidget item={item} />
			<LarianTechAssetWidget assetId={item?.id} />
			<TimelineWidget item={item} />
			<ContractWidget item={item} />
		</div>
	);
};
